import { useCallback, useEffect, useRef } from 'react';
import type { JSONContent } from '@tiptap/react';
import { useAuthStore } from '../stores/useAuthStore';

const RECENT_INPUT_STORAGE_PREFIX = 'borgee:composer:recent-input:';
const MAX_RECENT_INPUTS = 20;

interface StoredRecentInputEntry {
  markdown: string;
  doc?: JSONContent;
}

interface StoredRecentInputs {
  inputs: StoredRecentInputEntry[];
}

export interface RecentInputEntry {
  markdown: string;
  doc: JSONContent | null;
}

const storageKey = (accountId: string, scopeKey: string): string =>
  `${RECENT_INPUT_STORAGE_PREFIX}${accountId}:${scopeKey}`;

const normalizeStoredRecentInputEntry = (entry: unknown): RecentInputEntry | null => {
  if (typeof entry === 'string') {
    const markdown = entry.trim();
    return markdown.length === 0 ? null : { markdown, doc: null };
  }
  if (typeof entry !== 'object' || entry === null) return null;
  const markdown = typeof (entry as { markdown?: unknown }).markdown === 'string'
    ? (entry as { markdown: string }).markdown.trim()
    : '';
  if (markdown.length === 0) return null;
  const doc = (entry as { doc?: unknown }).doc;
  return typeof doc === 'object' && doc !== null
    ? { markdown, doc: doc as JSONContent }
    : { markdown, doc: null };
};

const readStoredRecentInputs = (accountId: string, scopeKey: string): RecentInputEntry[] => {
  try {
    const raw = window.sessionStorage.getItem(storageKey(accountId, scopeKey));
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as StoredRecentInputs;
    if (!Array.isArray(parsed?.inputs)) {
      return [];
    }
    return parsed.inputs
      .map(normalizeStoredRecentInputEntry)
      .filter((entry): entry is RecentInputEntry => entry !== null)
      .slice(0, MAX_RECENT_INPUTS);
  } catch {
    return [];
  }
};

const writeStoredRecentInputs = (accountId: string, scopeKey: string, inputs: RecentInputEntry[]): void => {
  try {
    const serializedInputs: StoredRecentInputEntry[] = inputs
      .slice(0, MAX_RECENT_INPUTS)
      .map((entry): StoredRecentInputEntry => (
        entry.doc === null
          ? { markdown: entry.markdown }
          : { markdown: entry.markdown, doc: entry.doc }
      ));
    window.sessionStorage.setItem(storageKey(accountId, scopeKey), JSON.stringify({
      inputs: serializedInputs,
    } satisfies StoredRecentInputs));
  } catch {
    // Recent-input history is a convenience and must not block typing or sends.
  }
};

export interface RecentInputHistory {
  recordSentInput: (entry: RecentInputEntry) => void;
  recallOlderInput: (currentInput: string) => RecentInputEntry | null;
  recallNewerInput: () => RecentInputEntry | null;
  clearRecallState: () => void;
  hasActiveRecall: () => boolean;
}

export function useRecentInputHistory(scopeKey: string): RecentInputHistory {
  const accountId = useAuthStore(s => s.currentUser?.id ?? null);
  const recalledIndexRef = useRef<number | null>(null);
  const draftBeforeRecallRef = useRef('');

  const clearRecallState = useCallback(() => {
    recalledIndexRef.current = null;
    draftBeforeRecallRef.current = '';
  }, []);

  useEffect(() => clearRecallState, [clearRecallState, accountId]);

  const recordSentInput = useCallback((entry: RecentInputEntry) => {
    if (accountId === null) return;
    const normalizedMarkdown = entry.markdown.trim();
    if (normalizedMarkdown.length === 0) return;
    const nextInputs: RecentInputEntry[] = [
      {
        markdown: normalizedMarkdown,
        doc: entry.doc,
      },
      ...readStoredRecentInputs(accountId, scopeKey).filter((stored) => stored.markdown !== normalizedMarkdown),
    ];
    writeStoredRecentInputs(accountId, scopeKey, nextInputs);
    clearRecallState();
  }, [accountId, clearRecallState, scopeKey]);

  const recallOlderInput = useCallback((currentInput: string) => {
    if (accountId === null) return null;
    const inputs = readStoredRecentInputs(accountId, scopeKey);
    if (inputs.length === 0) return null;
    if (recalledIndexRef.current === null) {
      draftBeforeRecallRef.current = currentInput;
      recalledIndexRef.current = 0;
      return inputs[0] ?? null;
    }
    const nextIndex = Math.min(recalledIndexRef.current + 1, inputs.length - 1);
    if (nextIndex === recalledIndexRef.current) {
      return inputs[nextIndex] ?? null;
    }
    recalledIndexRef.current = nextIndex;
    return inputs[nextIndex] ?? null;
  }, [accountId, scopeKey]);

  const recallNewerInput = useCallback(() => {
    if (accountId === null || recalledIndexRef.current === null) return null;
    if (recalledIndexRef.current === 0) {
      const restoredDraft = draftBeforeRecallRef.current;
      clearRecallState();
      return { markdown: restoredDraft, doc: null };
    }
    const inputs = readStoredRecentInputs(accountId, scopeKey);
    const nextIndex = recalledIndexRef.current - 1;
    recalledIndexRef.current = nextIndex;
    return inputs[nextIndex] ?? null;
  }, [accountId, clearRecallState, scopeKey]);

  return {
    recordSentInput,
    recallOlderInput,
    recallNewerInput,
    clearRecallState,
    hasActiveRecall: () => recalledIndexRef.current !== null,
  };
}
